import { useMemo } from 'react';
import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  memberService, 
  CreateMemberDto, 
  UpdateMemberDto,
  UpdateProfileDto 
} from '../services';
import { CURRENT_MEMBER_QUERY_KEY } from '../lib/queryKeys';
import { isAdminRole, isResourceAdminRole, isManagerRole } from '../utils/roles';
import { useAuth } from '../contexts/AuthContext';
import { Member, OrgChartNode, SecurityRole } from '../types';

// Re-export deprecated aliases for backward compatibility
export type { CreateMemberDto as CreateUserDto } from '../services';
export type { UpdateMemberDto as UpdateUserDto } from '../services';

/**
 * Query Keys
 * Centralized query key factory for members
 */
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (includeInactive?: boolean) => [...memberKeys.lists(), includeInactive] as const,
  details: () => [...memberKeys.all, 'detail'] as const,
  detail: (id: string) => [...memberKeys.details(), id] as const,
  me: () => CURRENT_MEMBER_QUERY_KEY,
  myDirectReports: () => [...memberKeys.all, 'my-direct-reports'] as const,
  clientTags: () => [...memberKeys.all, 'client-tags'] as const,
  upcomingBirthdays: (days?: number) => [...memberKeys.all, 'birthdays', days] as const,
  upcomingAnniversaries: (days?: number) => [...memberKeys.all, 'anniversaries', days] as const,
  orgChart: () => [...memberKeys.all, 'org-chart'] as const,
  reportingChain: (userId: string) => [...memberKeys.all, 'reporting-chain', userId] as const,
  myReportingChain: () => [...memberKeys.all, 'reporting-chain', 'me'] as const,
};

/** @deprecated Use memberKeys */
export const userKeys = memberKeys;

/** How long the authorization role from /members/me is considered fresh. */
const SESSION_ROLE_STALE_TIME_MS = 60 * 1000;

/**
 * Writes the member returned by a self-service mutation into the /members/me cache without
 * dropping `securityRole`: only GET /members/me carries it, so a plain overwrite would leave
 * the session with no role until the next refetch.
 */
const setCurrentMemberCache = (queryClient: QueryClient, updatedUser: Member): void => {
  queryClient.setQueryData<Member>(memberKeys.me(), (previous) =>
    previous
      ? { ...previous, ...updatedUser, securityRole: updatedUser.securityRole ?? previous.securityRole }
      : updatedUser
  );
};

/**
 * Hook: useSessionRole
 * Single source of truth for the current user's authorization role.
 *
 * The role is fetched from `GET /members/me` (`securityRole`, the exact value the backend
 * uses for every permission check) and re-validated at most once a minute and on window
 * focus, so a role change no longer requires a sign-out to take effect. The login-time role
 * cached in AuthContext is used only as a first-paint hint while that request is in flight.
 */
export const useSessionRole = (): { role: string | null; isLoading: boolean } => {
  const { authOrgRole, user, loading } = useAuth();

  const { data, isPending } = useQuery({
    queryKey: memberKeys.me(),
    queryFn: () => memberService.getMe(),
    enabled: !!user,
    staleTime: SESSION_ROLE_STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const role = data?.securityRole ?? authOrgRole ?? null;
  return { role, isLoading: loading || (!!user && isPending && !authOrgRole) };
};

/**
 * Hook: useIsAdmin
 * Admin status (billing-inclusive) derived from the server-resolved session role.
 */
export const useIsAdmin = (): { isAdmin: boolean; isLoading: boolean } => {
  const { role, isLoading } = useSessionRole();
  return { isAdmin: isAdminRole(role), isLoading };
};

/**
 * Hook: useIsResourceAdmin
 * Resource-management admin status (PTO, onboarding, offboarding, kudos): true only
 * for OWNER/ADMIN, excluding BILLING — mirrors the backend ADMIN bypass threshold for
 * these flows. Use this instead of useIsAdmin for resource-management surfaces so the
 * UI matches what the backend actually authorizes.
 */
export const useIsResourceAdmin = (): { isAdmin: boolean; isLoading: boolean } => {
  const { role, isLoading } = useSessionRole();
  return { isAdmin: isResourceAdminRole(role), isLoading };
};

/**
 * Hook: useIsManager
 * Manager status derived from the server-resolved session role.
 */
export const useIsManager = (): { isManager: boolean; isLoading: boolean } => {
  const { role, isLoading } = useSessionRole();
  return { isManager: isManagerRole(role), isLoading };
};

/**
 * Hook: useMyDirectReports
 * Fetch direct reports of the current member
 */
export const useMyDirectReports = () => {
  return useQuery({
    queryKey: memberKeys.myDirectReports(),
    queryFn: () => memberService.getMyDirectReports(),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useCurrentUser
 * Fetch current user profile from /members/me.
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: memberKeys.me(),
    queryFn: () => memberService.getMe(),
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook: useUsers
 * Fetch all members
 */
export const useUsers = (includeInactive: boolean = false) => {
  return useQuery({
    queryKey: memberKeys.list(includeInactive),
    queryFn: () => memberService.getAll(includeInactive),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useUser
 * Fetch a single member by ID
 */
export const useUser = (id: string) => {
  return useQuery({
    queryKey: memberKeys.detail(id),
    queryFn: () => memberService.getById(id),
    enabled: !!id,
  });
};

/**
 * Hook: useClientTags
 * Fetch all unique client tags
 */
export const useClientTags = () => {
  return useQuery({
    queryKey: memberKeys.clientTags(),
    queryFn: () => memberService.getClientTags(),
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook: useUpcomingBirthdays
 * Fetch upcoming birthdays
 */
export const useUpcomingBirthdays = (days: number = 7) => {
  return useQuery({
    queryKey: memberKeys.upcomingBirthdays(days),
    queryFn: () => memberService.getUpcomingBirthdays(days),
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useUpcomingAnniversaries
 * Fetch upcoming work anniversaries
 */
export const useUpcomingAnniversaries = (days: number = 30) => {
  return useQuery({
    queryKey: memberKeys.upcomingAnniversaries(days),
    queryFn: () => memberService.getUpcomingAnniversaries(days),
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useCreateUser
 * Create a new member
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMemberDto) => memberService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
    },
  });
};

/**
 * Hook: useUpdateUser
 * Update a member
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMemberDto }) =>
      memberService.update(id, data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(memberKeys.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: memberKeys.myDirectReports() });
    },
  });
};

/**
 * Hook: useUpdateUserRole
 * Change the authorization role a member holds in the organization (admin only). The member
 * detail is refetched so the profile shows the role the server now resolves.
 */
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: SecurityRole }) =>
      memberService.updateRole(id, role),
    onSuccess: (_updated, { id }) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
    },
  });
};

/**
 * Hook: useUpdateProfile
 * Update current member's profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileDto) => memberService.updateProfile(data),
    onSuccess: (updatedUser) => {
      setCurrentMemberCache(queryClient, updatedUser);
      queryClient.setQueryData(memberKeys.detail(updatedUser.id), updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    },
  });
};

/**
 * Hook: useDeleteUser
 * Delete a member
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => memberService.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: memberKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: memberKeys.myDirectReports() });
    },
  });
};

/**
 * Hook: useUploadOwnPhoto
 * Upload own profile photo
 */
export const useUploadOwnPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => memberService.uploadOwnPhoto(file),
    onSuccess: (updatedUser) => {
      setCurrentMemberCache(queryClient, updatedUser);
      queryClient.setQueryData(memberKeys.detail(updatedUser.id), updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    },
  });
};

/**
 * Hook: useUploadUserPhoto
 * Upload member photo (admin only)
 */
export const useUploadUserPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      memberService.uploadPhoto(id, file),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(memberKeys.detail(updatedUser.id), updatedUser);
    },
  });
};

/**
 * Hook: useSyncUserRoles
 * Sync all member roles with organizational role admin flags
 */
export const useSyncUserRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => memberService.syncRoles(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
    },
  });
};

/**
 * Hook: useOrgChart
 * Fetch organizational chart hierarchy
 */
export const useOrgChart = () => {
  return useQuery({
    queryKey: memberKeys.orgChart(),
    queryFn: () => memberService.getOrgChart(),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
};

/**
 * Hook: useReportingChain
 * Fetch reporting chain for a specific member
 */
export const useReportingChain = (userId: string) => {
  return useQuery({
    queryKey: memberKeys.reportingChain(userId),
    queryFn: () => memberService.getReportingChain(userId),
    enabled: !!userId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useMyReportingChain
 * Fetch reporting chain for the current member
 */
export const useMyReportingChain = () => {
  return useQuery({
    queryKey: memberKeys.myReportingChain(),
    queryFn: () => memberService.getMyReportingChain(),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Hook: useMyReportSubtreeIds
 * Returns the Set of member ids in the current user's reporting subtree
 * (all transitive reports + the user themselves).
 */
export const useMyReportSubtreeIds = (): { subtreeIds: Set<string>; isLoading: boolean } => {
  const { user } = useAuth();
  const { data: orgChart, isLoading: orgChartLoading } = useOrgChart();

  const subtreeIds = useMemo(() => {
    if (!user?.id || !orgChart) {
      return new Set<string>();
    }

    const findNode = (nodes: OrgChartNode[], targetId: string): OrgChartNode | null => {
      const queue = [...nodes];
      while (queue.length > 0) {
        const node = queue.shift()!;
        if (node.id === targetId) return node;
        queue.push(...node.children);
      }
      return null;
    };

    const myNode = findNode(orgChart, user.id);
    const ids = new Set<string>([user.id]);

    if (myNode) {
      const queue = [...myNode.children];
      while (queue.length > 0) {
        const node = queue.shift()!;
        ids.add(node.id);
        queue.push(...node.children);
      }
    }

    return ids;
  }, [user?.id, orgChart]);

  return { subtreeIds, isLoading: orgChartLoading };
};
